import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/** Language the user picked with the language switcher (persisted by it), or English. */
function storedLanguage(): 'pt' | 'en' {
  try {
    const stored = localStorage.getItem('i18nextLng')?.split('-')[0];
    if (stored === 'en' || stored === 'pt') return stored;
  } catch {
    // storage unavailable (private mode): fall through to the default
  }
  return 'en';
}

// Only the namespaces every page needs are bundled into the entry chunk (English, the default
// language); everything else is fetched on demand by the lazy backend below, so the landing/login
// pages do not download ~400 kB of translations they never render.
import commonEN from '../locales/en/common.json';
import authEN from '../locales/en/auth.json';
import menuEN from '../locales/en/menu.json';
import rolesEN from '../locales/en/roles.json';
import landingEN from '../locales/en/landing.json';
import notificationEN from '../locales/en/notification.json';
import { createLazyBackend, namespacesFor } from './lazyBackend';

/**
 * i18next Configuration for Internationalization
 * 
 * This configuration sets up the translation system for the application:
 * 
 * Features:
 * - Automatic language detection from browser settings
 * - Fallback to English as default
 * - Support for Portuguese and English with namespace organization
 * - LocalStorage persistence of language preference
 * - Namespace-based organization (common, auth, menu, roles)
 * 
 * Usage in components:
 * ```tsx
 * import { useTranslation } from 'react-i18next';
 * 
 * // Default namespace (common)
 * const { t } = useTranslation();
 * return <button>{t('buttons.save')}</button>;
 * 
 * // Specific namespace
 * const { t } = useTranslation('auth');
 * return <h1>{t('login.title')}</h1>;
 * ```
 */

/** Namespaces bundled with the entry chunk: the ones the always-mounted shell, landing and login use. */
export const EAGER_NAMESPACES = ['common', 'auth', 'menu', 'roles', 'landing', 'notification'];

// Initialize i18next
i18n
  // Fetch every other namespace (and every other language) on demand
  .use(createLazyBackend())
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize with configuration
  .init({
    // English, the default language, is bundled for the eager namespaces only
    resources: {
      en: {
        common: commonEN,
        auth: authEN,
        menu: menuEN,
        roles: rolesEN,
        landing: landingEN,
        notification: notificationEN,
      },
    },
    // Without this i18next would never ask the backend when `resources` is set
    partialBundledLanguages: true,

    // English by default; the choice made with the language switcher is remembered
    lng: storedLanguage(),

    // Default language fallback
    fallbackLng: 'en',

    // Default namespace
    defaultNS: 'common',

    // Namespaces loaded up front; any other one is added when a component first asks for it
    ns: [...EAGER_NAMESPACES], // copy: i18next appends every namespace it loads to this array

    // Available languages
    supportedLngs: ['pt', 'en'],

    // Interpolation options
    interpolation: {
      // React already escapes values to prevent XSS
      escapeValue: false,
    },

    // React-specific options
    react: {
      // Wait for translations to load before rendering
      useSuspense: true,
    },

    // Debug mode (enabled in development)
    debug: false,
  });


/**
 * Namespaces a route's pages translate with, keyed by the first path segment. They are requested
 * as soon as the entry chunk runs, in parallel with the route chunk, instead of only once the page
 * renders and suspends on them. Anything not listed (shared components, other roles) is still
 * fetched on demand, just one round trip later.
 */
const NAMESPACES_BY_FIRST_SEGMENT: Record<string, string[]> = {
  participant: ['participant', 'dashboard', 'session', 'questionnaire', 'assignments'],
  trainer: ['trainer', 'session', 'assignments', 'ai'],
  admin: ['admin', 'questionnaire', 'programme'],
  mentor: ['mentor'],
  manager: ['manager'],
  company: ['companyAdmin'],
  backoffice: ['backoffice'],
  content: ['content'],
  courses: ['content'],
  notifications: ['notification'],
  monitoring: ['monitoring'],
  'ai-assistant': ['ai'],
  'check-in': ['monitoring'],
  reports: ['monitoring'],
  gdpr: ['gdpr'],
  privacy: ['gdpr'],
  faq: ['faq'],
  support: ['support'],
  about: ['about'],
  terms: ['terms'],
  'privacy-policy': ['legal'],
  cookies: ['legal'],
};

/** Namespaces to start loading for a URL path (those not already bundled). */
export function namespacesForPath(pathname: string): string[] {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  return (NAMESPACES_BY_FIRST_SEGMENT[first] ?? []).filter((ns) => !EAGER_NAMESPACES.includes(ns));
}

/** Starts fetching every namespace that is not bundled, so later navigation finds them cached. */
export function prefetchNamespaces(): void {
  const rest = namespacesFor(i18n.language || 'en').filter((ns) => !EAGER_NAMESPACES.includes(ns));
  void i18n.loadNamespaces(rest);
}

/**
 * Landing and login (the pages where the first paint matters most and that need none of the lazy
 * namespaces) fetch the rest only once the page has loaded and the browser is idle, so the
 * translations never compete with the scripts that paint the page.
 */
const PUBLIC_FIRST_PAINT_PATHS = ['/', '/login'];

if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
  if (PUBLIC_FIRST_PAINT_PATHS.includes(window.location.pathname)) {
    const afterLoad = () => {
      const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
      if (idle) idle(prefetchNamespaces);
      else setTimeout(prefetchNamespaces, 200);
    };
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  } else {
    const needed = namespacesForPath(window.location.pathname);
    if (needed.length > 0) void i18n.loadNamespaces(needed);
  }
}

export default i18n;
